import axios from 'axios';
import { UserCredentials, SafeDatabaseUser } from '../types/types';
import api from './config';

const USER_API_URL = `${process.env.REACT_APP_SERVER_URL}/user`;

/**
 * Function to get all users
 *
 * @throws Error if there is an issue fetching users.
 */
const getUsers = async (): Promise<SafeDatabaseUser[]> => {
  const res = await api.get(`${USER_API_URL}`);
  if (res.status !== 200) {
    throw new Error('Error when fetching users');
  }
  return res.data;
};

/**
 * Function to get users
 *
 * @throws Error if there is an issue fetching users.
 */
const getUserByUsername = async (username: string): Promise<SafeDatabaseUser> => {
  const res = await api.get(`${USER_API_URL}/${username}`);
  if (res.status !== 200) {
    throw new Error('Error when fetching user');
  }
  return res.data;
};

/**
 * Sends a POST request to create a new user account.
 *
 * @param user - The user credentials (username, password, and email) for signup.
 * @returns {Promise<User>} The newly created user object.
 * @throws {Error} If an error occurs during the signup process.
 */
const createUser = async (
  user: UserCredentials,
): Promise<SafeDatabaseUser & { requiresTwoFactor?: boolean; signupToken?: string }> => {
  try {
    const res = await api.post(`${USER_API_URL}`, user);
    return res.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(`Error while signing up: ${error.response.data}`);
    } else {
      throw new Error('Error while signing up');
    }
  }
};

/**
 * Sends a POST request to authenticate a user.
 *
 * @param user - The user credentials (username and password) for login.
 * @returns {Promise<User>} The authenticated user object.
 * @throws {Error} If an error occurs during the login process.
 */
const loginUser = async (user: UserCredentials): Promise<SafeDatabaseUser> => {
  try {
    const res = await api.post(`${USER_API_URL}/login`, user);
    return res.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      // Check if this is a banned user error (status 403 with isBanned field)
      if (error.response.status === 403 && error.response.data && error.response.data.isBanned) {
        const banReason = error.response.data.banReason || 'Violation of community guidelines';
        const banDate = error.response.data.bannedDate
          ? new Date(error.response.data.bannedDate).toLocaleDateString()
          : 'unknown date';
        throw new Error(
          `Your account has been banned for: ${banReason}. Banned on: ${banDate}. Please contact an administrator if you believe this is an error.`,
        );
      }
      throw new Error(`Error while logging in: ${error.response.data}`);
    } else {
      throw new Error('Error while logging in');
    }
  }
};

/**
 * Deletes a user by their username.
 * @param username - The unique username of the user
 * @returns A promise that resolves to the deleted user data
 * @throws {Error} If the request to the server is unsuccessful
 */
const deleteUser = async (username: string): Promise<SafeDatabaseUser> => {
  const res = await api.delete(`${USER_API_URL}/${username}`);
  if (res.status !== 200) {
    throw new Error('Error when deleting user');
  }
  return res.data;
};

/**
 * Resets the password for a user.
 * @param username - The unique username of the user
 * @param newPassword - The new password to be set for the user
 * @returns A promise that resolves to the updated user data
 * @throws {Error} If the request to the server is unsuccessful
 */
const resetPassword = async (username: string, newPassword: string): Promise<SafeDatabaseUser> => {
  const res = await api.put(`${USER_API_URL}/resetPassword`, {
    username,
    password: newPassword,
  });
  if (res.status !== 200) {
    throw new Error('Error when resetting password');
  }
  return res.data;
};

/**
 * Updates the user's biography.
 * @param username The unique username of the user
 * @param newBiography The new biography to set for this user
 * @returns A promise resolving to the updated user
 * @throws Error if the request fails
 */
const updateBiography = async (
  username: string,
  newBiography: string,
): Promise<SafeDatabaseUser> => {
  const res = await api.put(`${USER_API_URL}/updateBiography`, {
    username,
    biography: newBiography,
  });
  if (res.status !== 200) {
    throw new Error('Error when updating biography');
  }
  return res.data;
};

/**
 * Updates the user's email.
 * @param username The unique username of the user
 * @param newEmail The new email to set for this user
 * @returns A promise resolving to the updated user
 * @throws Error if the request fails
 */
const updateEmail = async (username: string, newEmail: string): Promise<SafeDatabaseUser> => {
  const res = await api.put(`${USER_API_URL}/updateEmail`, {
    username,
    email: newEmail,
  });
  if (res.status !== 200) {
    throw new Error('Error when updating email');
  }
  return res.data;
};

/**
 * Follows a user.
 * @param follower - The username of the user who is following
 * @param following - The username of the user to follow
 * @returns A promise that resolves to the updated user data
 * @throws {Error} If the request to the server is unsuccessful
 */
const followUser = async (follower: string, following: string): Promise<SafeDatabaseUser> => {
  const res = await api.post(`${USER_API_URL}/follow`, { follower, following });
  if (res.status !== 200) {
    throw new Error('Error when following user');
  }
  return res.data;
};

/**
 * Unfollows a user.
 * @param follower - The username of the user who is unfollowing
 * @param following - The username of the user to unfollow
 * @returns A promise that resolves to the updated user data
 * @throws {Error} If the request to the server is unsuccessful
 */
const unfollowUser = async (follower: string, following: string): Promise<SafeDatabaseUser> => {
  const res = await api.post(`${USER_API_URL}/unfollow`, { follower, following });
  if (res.status !== 200) {
    throw new Error('Error when unfollowing user');
  }
  return res.data;
};

/**
 * Sends a POST request to log the user out.
 *
 * @returns {Promise<void>} A promise that resolves when logout is successful.
 * @throws {Error} If an error occurs during the logout process.
 */
const logoutUser = async (): Promise<void> => {
  try {
    await api.post(`${USER_API_URL}/logout`);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(`Error while logging out: ${error.response.data}`);
    } else {
      throw new Error('Error while logging out');
    }
  }
};

/**
 * Sets up two-factor authentication for a user.
 * @param username - The username of the user
 * @param email - The email address to send 2FA codes to
 * @returns A promise that resolves to the setup result
 * @throws {Error} If the request fails
 */
const setupTwoFactor = async (
  username: string,
  email: string,
): Promise<{ message: string; success: boolean }> => {
  try {
    const res = await api.post(`${USER_API_URL}/2fa/setup`, { username, email });
    return res.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(`Error setting up 2FA: ${error.response.data}`);
    } else {
      throw new Error('Error setting up 2FA');
    }
  }
};

/**
 * Enables two-factor authentication for a user after verification.
 * @param username - The username of the user
 * @param twoFactorToken - The verification token
 * @returns A promise that resolves to the enable result
 * @throws {Error} If the request fails
 */
const enableTwoFactor = async (
  username: string,
  twoFactorToken: string,
): Promise<{ message: string; user: SafeDatabaseUser }> => {
  try {
    const res = await api.post(`${USER_API_URL}/2fa/enable`, { username, twoFactorToken });
    return res.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(`Error enabling 2FA: ${error.response.data}`);
    } else {
      throw new Error('Error enabling 2FA');
    }
  }
};

/**
 * Verifies a two-factor authentication token during login.
 * @param username - The username of the user
 * @param twoFactorToken - The verification token
 * @returns A promise that resolves to the authenticated user
 * @throws {Error} If the request fails
 */
const verifyTwoFactor = async (
  username: string,
  twoFactorToken: string,
): Promise<SafeDatabaseUser> => {
  try {
    const res = await api.post(`${USER_API_URL}/2fa/verify`, { username, twoFactorToken });
    return res.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(`Error verifying 2FA: ${error.response.data}`);
    } else {
      throw new Error('Error verifying 2FA');
    }
  }
};

/**
 * Completes the signup process after 2FA verification
 *
 * @param username - The username of the account being created
 * @param twoFactorToken - The verification code
 * @param signupToken - The token from the initial signup request
 * @returns {Promise<SafeDatabaseUser>} The created user
 * @throws {Error} If verification fails
 */
const completeSignup = async (
  username: string,
  twoFactorToken: string,
  signupToken: string,
): Promise<SafeDatabaseUser> => {
  try {
    const res = await api.post(`${USER_API_URL}/complete-signup`, {
      username,
      twoFactorToken,
      signupToken,
    });
    return res.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(`${error.response.data}`);
    } else {
      throw new Error('Error completing signup');
    }
  }
};

/**
 * Updates the user's visibility.
 * @param username The unique username of the user
 * @param visibility The new visibility to set for this user
 * @returns A promise resolving to the updated user
 * @throws Error if the request fails
 */
const updateVisibility = async (
  username: string,
  newVisibility: string,
): Promise<SafeDatabaseUser> => {
  // eslint-disable-next-line no-useless-catch
  try {
    const res = await api.put(`${USER_API_URL}/updateVisibility`, {
      // This needs to match the expected structure in isUpdateVisibilityBodyValid
      username,
      visibility: newVisibility,
    });

    if (!res.data || res.status !== 200) {
      throw new Error('Error when updating visibility');
    }

    return res.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Switches theme between light and dark mode.
 * @param theme The new theme to set
 * @returns A promise resolving to the updated user
 * @throws Error if the request fails
 */
const updateTheme = async (
  username: string,
  newTheme: 'light' | 'dark',
): Promise<SafeDatabaseUser> => {
  const res = await api.patch(`${USER_API_URL}/updateTheme`, {
    username,
    theme: newTheme,
  });

  if (res.status !== 200) {
    throw new Error('Error when updating theme');
  }
  return res.data;
};

const guestLogin = async () => {
  try {
    // Make sure this path matches exactly what your backend is expecting
    const res = await api.post(`${USER_API_URL}/guest`);
    return res.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      // Add more detailed error handling
      const errorMessage =
        typeof error.response.data === 'string'
          ? error.response.data
          : JSON.stringify(error.response.data);
      throw new Error(`Error while logging in as guest: ${errorMessage}`);
    } else {
      throw new Error('Error while logging in as guest');
    }
  }
};
/**
 * Report a user for inappropriate behavior.
 *
 * @param reporterId - Username of the user making the report
 * @param reportedUser - Username of the user being reported
 * @param reason - Reason for the report
 * @returns The created report or an error
 */
export const reportUser = async (
  reporterId: string,
  reportedUser: string,
  reason: string,
): Promise<{ error?: string; [key: string]: unknown }> => {
  try {
    const response = await api.post(`${USER_API_URL}/report`, {
      reporterId,
      reportedUser,
      reason,
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return { error: error.response.data };
    }
    return { error: error instanceof Error ? error.message : 'An unknown error occurred' };
  }
};

/**
 * Fetch all reports for admin review.
 *
 * @param currentUsername Optional username to use for authentication if cookies aren't working
 * @returns A list of all reports or an error
 */
export const getAllReports = async (currentUsername?: string): Promise<unknown> => {
  let username: string | undefined;

  // Method 1: Try using the passed username parameter (useful for contexts where cookie access is not reliable)
  if (currentUsername) {
    username = currentUsername;
  }
  // Method 2: Try getting from cookie
  else {
    // Get the current user from the cookie
    const cookies = document.cookie;
    const usernameCookie = cookies.split('; ').find(row => row.startsWith('fakeSO_user='));

    if (usernameCookie) {
      // The cookie contains the full user object as JSON, so we need to parse it
      const cookieValue = usernameCookie.split('=')[1];
      try {
        // URL decode the cookie value and parse it as JSON
        const decodedValue = decodeURIComponent(cookieValue);
        const userObject = JSON.parse(decodedValue);
        username = userObject.username;
      } catch (err) {
        // Silent error handling
      }
    }
    // Method 3: Try localStorage as a fallback
    else {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          username = parsedUser.username;
        } catch (err) {
          // Silent error handling
        }
      }
    }
  }

  // If we still don't have a username, we can't authenticate
  if (!username) {
    throw new Error('Not authenticated - no username found');
  }

  // Make sure the username doesn't need encoding
  const authUsername = encodeURIComponent(username);

  // Method 1: Standard "Bearer username" format
  try {
    const response = await api.get(`${USER_API_URL}/reports`, {
      headers: {
        Authorization: `Bearer ${authUsername}`,
      },
    });

    return response.data;
  } catch (axiosError) {
    if (axios.isAxiosError(axiosError) && axiosError.response?.status === 401) {
      // Method 2: Try with just the username as fallback
      const response2 = await api.get(`${USER_API_URL}/reports`, {
        headers: {
          Authorization: authUsername,
        },
      });

      return response2.data;
    }

    if (axios.isAxiosError(axiosError)) {
      // Rethrow with more details
      throw new Error(`Error fetching reports: ${axiosError.response?.data || axiosError.message}`);
    }
    throw axiosError;
  }
};

/**
 * Fetch pending reports that need admin review.
 *
 * @param currentUsername Optional username to use for authentication if cookies aren't working
 * @returns A list of pending reports or an error
 */
export const getPendingReports = async (currentUsername?: string): Promise<unknown> => {
  try {
    let username: string | undefined;

    // Method 1: Try using the passed username parameter
    if (currentUsername) {
      username = currentUsername;
    }
    // Method 2: Try getting from cookie
    else {
      const usernameCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('fakeSO_user='));

      if (usernameCookie) {
        // The cookie contains the full user object as JSON, so we need to parse it
        const cookieValue = usernameCookie.split('=')[1];
        try {
          // URL decode the cookie value and parse it as JSON
          const decodedValue = decodeURIComponent(cookieValue);
          const userObject = JSON.parse(decodedValue);
          username = userObject.username;
        } catch (err) {
          // Silently handle error to avoid ESLint warnings
        }
      }
      // Method 3: Try localStorage as a fallback
      else {
        const storedUser = localStorage.getItem('user');

        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            username = parsedUser.username;
          } catch (err) {
            // Silently handle error to avoid ESLint warnings
          }
        }
      }
    }

    // If we still don't have a username, we can't authenticate
    if (!username) {
      throw new Error('Not authenticated - no username found');
    }

    // Make the request with the Authorization header
    const response = await api.get(`${USER_API_URL}/reports/pending`, {
      headers: {
        Authorization: `Bearer ${username}`,
      },
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(`Error fetching pending reports: ${error.response.data}`);
    }
    throw new Error('Error fetching pending reports');
  }
};

/**
 * Update a report's status after admin review.
 *
 * @param reportId - The ID of the report to update
 * @param status - The new status ('reviewed', 'resolved', or 'dismissed')
 * @param reviewNotes - Notes added by the admin during review
 * @param currentUsername - Optional username to use for authentication if cookies aren't working
 * @returns The updated report or an error
 */
export const updateReportStatus = async (
  reportId: string,
  status: 'reviewed' | 'resolved' | 'dismissed',
  reviewNotes: string,
  currentUsername?: string,
): Promise<unknown> => {
  try {
    let username: string | undefined;

    // Method 1: Try using the passed username parameter
    if (currentUsername) {
      username = currentUsername;
    }
    // Method 2: Try getting from cookie
    else {
      const usernameCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('fakeSO_user='));

      if (usernameCookie) {
        // The cookie contains the full user object as JSON, so we need to parse it
        const cookieValue = usernameCookie.split('=')[1];
        try {
          // URL decode the cookie value and parse it as JSON
          const decodedValue = decodeURIComponent(cookieValue);
          const userObject = JSON.parse(decodedValue);
          username = userObject.username;
        } catch (err) {
          // Silently handle error to avoid ESLint warnings
        }
      }
      // Method 3: Try localStorage as a fallback
      else {
        const storedUser = localStorage.getItem('user');

        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            username = parsedUser.username;
          } catch (err) {
            // Silently handle error to avoid ESLint warnings
          }
        }
      }
    }

    // If we still don't have a username, we can't authenticate
    if (!username) {
      throw new Error('Not authenticated - no username found');
    }

    // Make the request with the Authorization header
    const response = await api.patch(
      `${USER_API_URL}/reports/update`,
      {
        reportId,
        status,
        reviewNotes,
      },
      {
        headers: {
          Authorization: `Bearer ${username}`,
        },
      },
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(`Error updating report: ${error.response.data}`);
    }
    throw new Error('Error updating report');
  }
};

/**
 * Ban a user account (admin only action).
 *
 * @param username - The username of the user to ban
 * @param reason - The reason for banning the user
 * @param currentUsername - Optional username to use for authentication if cookies aren't working
 * @returns The banned user data or an error
 */
export const banUser = async (
  username: string,
  reason: string,
  currentUsername?: string,
): Promise<unknown> => {
  try {
    let adminUsername: string | undefined;

    // Method 1: Try using the passed username parameter
    if (currentUsername) {
      adminUsername = currentUsername;
    }
    // Method 2: Try getting from cookie
    else {
      const usernameCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('fakeSO_user='));

      if (usernameCookie) {
        // The cookie contains the full user object as JSON, so we need to parse it
        const cookieValue = usernameCookie.split('=')[1];
        try {
          // URL decode the cookie value and parse it as JSON
          const decodedValue = decodeURIComponent(cookieValue);
          const userObject = JSON.parse(decodedValue);
          adminUsername = userObject.username;
        } catch (err) {
          // Silently handle error to avoid ESLint warnings
        }
      }
      // Method 3: Try localStorage as a fallback
      else {
        const storedUser = localStorage.getItem('user');

        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            adminUsername = parsedUser.username;
          } catch (err) {
            // Silently handle error to avoid ESLint warnings
          }
        }
      }
    }

    // If we still don't have a username, we can't authenticate
    if (!adminUsername) {
      throw new Error('Not authenticated - no username found');
    }

    // Make the request with the Authorization header
    const response = await api.post(
      `${USER_API_URL}/ban`,
      {
        username,
        reason,
      },
      {
        headers: {
          Authorization: `Bearer ${adminUsername}`,
        },
      },
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(`Error banning user: ${error.response.data}`);
    }
    throw new Error('Error banning user');
  }
};

/**
 * Issue a warning to a user account (admin only action).
 *
 * @param username - The username of the user to warn
 * @param reason - The reason for warning the user
 * @param currentUsername - Optional username to use for authentication if cookies aren't working
 * @returns The updated user data or an error
 */
export const warnUser = async (
  username: string,
  reason: string,
  currentUsername?: string,
): Promise<unknown> => {
  try {
    let adminUsername: string | undefined;

    // Method 1: Try using the passed username parameter
    if (currentUsername) {
      adminUsername = currentUsername;
    }
    // Method 2: Try getting from cookie
    else {
      const usernameCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('fakeSO_user='));

      if (usernameCookie) {
        // The cookie contains the full user object as JSON, so we need to parse it
        const cookieValue = usernameCookie.split('=')[1];
        try {
          // URL decode the cookie value and parse it as JSON
          const decodedValue = decodeURIComponent(cookieValue);
          const userObject = JSON.parse(decodedValue);
          adminUsername = userObject.username;
        } catch (err) {
          // Silently handle error to avoid ESLint warnings
        }
      }
      // Method 3: Try localStorage as a fallback
      else {
        const storedUser = localStorage.getItem('user');

        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            adminUsername = parsedUser.username;
          } catch (err) {
            // Silently handle error to avoid ESLint warnings
          }
        }
      }
    }

    // If we still don't have a username, we can't authenticate
    if (!adminUsername) {
      throw new Error('Not authenticated - no username found');
    }

    // Make the request with the Authorization header
    const response = await api.post(
      `${USER_API_URL}/warn`,
      {
        username,
        reason,
      },
      {
        headers: {
          Authorization: `Bearer ${adminUsername}`,
        },
      },
    );

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(`Error warning user: ${error.response.data}`);
    }
    throw new Error('Error warning user');
  }
};

export {
  getUsers,
  getUserByUsername,
  deleteUser,
  createUser,
  loginUser,
  resetPassword,
  updateBiography,
  updateEmail,
  followUser,
  unfollowUser,
  logoutUser,
  setupTwoFactor,
  enableTwoFactor,
  verifyTwoFactor,
  completeSignup,
  updateVisibility,
  updateTheme,
  guestLogin,
};

/**
 * Updates the user's profile picture URL in the database.
 * @param username The username of the user
 * @param profilePictureUrl The URL of the uploaded profile picture
 * @returns A promise resolving to the updated user
 * @throws Error if the request fails
 */
export const updateProfilePicture = async (
  username: string,
  profilePictureUrl: string,
): Promise<SafeDatabaseUser> => {
  const res = await api.put(`${USER_API_URL}/updateProfilePicture`, {
    username,
    profilePicture: profilePictureUrl,
  });
  if (res.status !== 200) {
    throw new Error('Error when updating profile picture');
  }
  return res.data;
};

/**
 * Refreshes the user data from the server and updates local storage.
 * This function should be called when we suspect the user data might have changed.
 *
 * @param username - The username of the user to refresh data for
 * @returns A promise that resolves to the refreshed user data
 */
export const refreshUserData = async (username: string): Promise<SafeDatabaseUser> => {
  try {
    const userData = await getUserByUsername(username);

    // Update localStorage
    localStorage.setItem('fakeSO_user', JSON.stringify(userData));

    // Update cookie
    document.cookie = `fakeSO_user=${encodeURIComponent(JSON.stringify(userData))}; path=/; max-age=${7 * 24 * 60 * 60}`;

    return userData;
  } catch (error) {
    throw new Error('Failed to refresh user data');
  }
};
